import { Component, OnInit } from '@angular/core';
import {
  GridDataResult,
  PagerSettings,
  DataStateChangeEvent,
} from '@progress/kendo-angular-grid';
import {
  LoaderService,
  MessageService,
  ColumnOptions,
  NamedEntity,
  GridState,
  ValidationResultService,
  GridStateCompositeFilterDescriptor,
} from '@shared';
import { GlobalEventsService, RestaurantService } from '@shared-data';
import { TasksService } from '../../services';
import { GridRowTasks } from '../../models';
import {
  AbstractControl,
  AbstractControlOptions,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { debounceTime, distinctUntilChanged, Subject, tap } from 'rxjs';
import { Restaurant } from '../../models/restaurant.model';

type SyncOption = 'All' | 'Specific' | 'ReportingUnit';
@Component({
  selector: 'app-tasks',
  templateUrl: './tasks.component.html',
  styleUrls: ['./tasks.component.scss'],
})
export class TasksComponent implements OnInit {
  selectedTaskDetails: any = null;
  tasks: GridDataResult = { data: [], total: 0 };
  columns: ColumnOptions[] = [];
  listOfApis: NamedEntity[] = [];

  pageSize = 10;
  pagerSettings: PagerSettings = {
    buttonCount: 3,
    info: true,
    type: 'numeric',
    previousNext: true,
  };
  state = new GridState(this.pageSize);

  taskApiForm!: FormGroup;

  restaurants: GridDataResult = { data: [], total: 0 };
  reportingUnits: GridDataResult = { data: [], total: 0 };

  restaurantSearch: Subject<string> = new Subject<string>();
  reportingUnitSearch: Subject<string> = new Subject<string>();

  selectedConcept: NamedEntity | null = null;
  constructor(
    private tasksService: TasksService,
    private messageService: MessageService,
    private loaderService: LoaderService,
    private restaurantService: RestaurantService,
    private formBuilder: FormBuilder,
    private validationResultService: ValidationResultService,
    private globalEventsService: GlobalEventsService,
  ) {
    this.columns = new GridRowTasks().getColumns();
  }

  ngOnInit(): void {
    this.setForm();
    this.subscribeToRestaurantSearch();
    this.subscribeToReportingUnitSearch();
    this.subscribeToConceptChanges();
  }

  subscribeToConceptChanges() {
    this.globalEventsService.selectedConcept$.subscribe((concept) => {
        this.selectedConcept = concept;
        this.setDefaultState();
    });
  }

  updateRestaurantSearch(value: string) {
    this.restaurantSearch.next(value);
  }

  updateReportingUnitSearch(value: string) {
    this.reportingUnitSearch.next(value);
  }

  subscribeToRestaurantSearch() {
    this.restaurantSearch
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        tap((searchText) => {
          this.onRestaurantFilter(searchText);
        }),
      )
      .subscribe();
  }

  subscribeToReportingUnitSearch() {
    this.reportingUnitSearch
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        tap((searchText) => {
          this.onReportingUnitFilter(searchText);
        }),
      )
      .subscribe();
  }

  setForm() {
    const formOptions: AbstractControlOptions = {
      validators: this.syncOptionValidator,
    };
    this.taskApiForm = this.formBuilder.group(
      {
        apiName: this.formBuilder.control(null, {
          validators: [Validators.required],
        }),
        syncOption: this.formBuilder.control('All', {
          validators: [Validators.required],
        }),
        restaurantNumbers: this.formBuilder.control([]),
        reportingUnit: this.formBuilder.control(null),
      },
      formOptions,
    );
  }

  readonly syncOptionValidator: ValidatorFn = (
    control: AbstractControl,
  ): ValidationErrors | null => {
    const form = control as FormGroup;
    const syncOption = form.get('syncOption')?.value as SyncOption;
    const restaurant = form.get('restaurantNumbers')?.value;
    const reportingUnit = form.get('reportingUnit')?.value;
    const apiName = form.get('apiName')?.value;

    if (syncOption === 'Specific' && (!restaurant || restaurant.length === 0)) {
      return {
        requireRestaurant:
          'At least one restaurant must be selected when syncOption is "Specific".',
      };
    }

    if (syncOption === 'ReportingUnit' && !reportingUnit) {
      return {
        requireReportingUnit:
          'Reporting Unit selection is required when syncOption is "ReportingUnit".',
      };
    }

    if (!apiName) {
      return { requireApi: 'API selection is required.' };
    }

    return null;
  };

  onDataStateChanged(event: DataStateChangeEvent): void {
    this.state = event as GridState;
    this.fetchTasks(this.state);
  }

  toggleExpand(item: any) {
    item._expanded = !item._expanded;
  }

  setDefaultState(): void {
    this.state = new GridState(this.pageSize, [
      { field: 'sentDt', dir: 'desc' },
    ]);
    this.state.filter = this.state.filter?.and({
      field: 'conceptKey',
      operator: 'eq',
      value: this.selectedConcept?.id,
    });

    this.resetForm();
    this.fetchTaskApiList();
    this.fetchTasks(this.state);
  }

  fetchTasks(state: GridState): void {
    this.loaderService.show();
    this.tasksService.getAllTasks(state).subscribe({
      next: (result: GridDataResult) => {
        this.tasks = result;
        this.loaderService.hide();
      },
      error: (error) => {
        const validationError = error?.error;
        if (validationError) {
          this.validationResultService.show(validationError);
        }
        this.messageService.error('Failed to load tasks');
        this.loaderService.hide();
      },
    });
  }

  fetchRestaurants(state: GridState): void {
    this.loaderService.show();
    this.restaurantService.getAllRestaurants(state).subscribe({
      next: (result) => {
        this.restaurants = result;
        this.restaurants.data = this.restaurants.data.map(
          (restaurant: Restaurant) => restaurant.name,
        );
        this.loaderService.hide();
      },
      error: (err) => {
        const validationError = err?.error;
        if (validationError) {
          this.validationResultService.show(validationError);
        }
        this.messageService.error('Failed to load restaurants');
        this.loaderService.hide();
      },
    });
  }

  fetchTaskApiList(): void {
    this.tasksService.getTaskApiList().subscribe({
      next: (result) => {
        this.listOfApis = result;
      },
      error: () => {
        this.messageService.error('Failed to load taskApis');
        this.loaderService.hide();
      },
    });
  }

  resetForm() {
    this.taskApiForm.reset();
  }

  onClickInvokeApI() {
    if ((this.taskApiForm.get('syncOption')?.value as SyncOption) == 'All') {
      this.taskApiForm.get('restaurantNumbers')?.setValue(null);
      this.taskApiForm.get('reportingUnit')?.setValue(null);
    }
    if (this.taskApiForm.invalid) {
      this.messageService.error('Please fill all required fields');
      return;
    }
    this.invokeApI();
  }

  invokeApI() {
    this.loaderService.show();
    const apiName = this.taskApiForm.get('apiName')?.value;
    const syncOption = this.taskApiForm.get('syncOption')?.value as SyncOption;
    const reportingUnit = this.taskApiForm.get('reportingUnit')?.value?.ruNo;
    const restaurantNumbers = this.taskApiForm.get('restaurantNumbers')?.value;
    this.tasksService
      .invokeAPI({
        apiName: apiName,
        restaurantNumbers: restaurantNumbers,
        reportingUnit: reportingUnit,
        syncOption: syncOption,
      })
      .subscribe({
        next: () => {
          this.setDefaultState();
          this.loaderService.hide();
          let message = 'Task message sent to queue: ';
          const syncOption = this.taskApiForm.get('syncOption')
            ?.value as SyncOption;
          if (syncOption === 'ReportingUnit') {
            message += `Reporting Unit: ${this.taskApiForm.get('reportingUnit')?.value?.ruNm}`;
          } else if (syncOption === 'Specific') {
            message += `Restaurant(s): ${this.taskApiForm.get('restaurantNumbers')?.value}`;
          } else {
            message += 'All';
          }
          this.messageService.success(message);
        },
        error: (err) => {
          const validationError = err?.error;
          if (validationError) {
            this.validationResultService.show(validationError);
          }
          this.messageService.error(`Failed to invoke task api: ${err.error}`);
          this.loaderService.hide();
        },
      });
  }

  openTaskDetails(taskId: string): void {
    this.loaderService.show();
    this.selectedTaskDetails = null;
    this.tasksService.getFlowerTaskDetails(taskId).subscribe({
      next: (result: any) => {
        this.selectedTaskDetails = { taskId: taskId, ...result };
        this.loaderService.hide();
      },
      error: () => {
        this.loaderService.hide();
        this.messageService.error(`Failed to load flower task ${taskId}`);
      },
    });
  }

  fetchReportingUnits(state: GridState): void {
    this.loaderService.show();
    this.restaurantService.getReportingUnits(state).subscribe({
      next: (result: GridDataResult) => {
        this.reportingUnits = result;
        this.loaderService.hide();
      },
      error: () => {
        this.messageService.error('Failed to load reporting units');
        this.loaderService.hide();
      },
    });
  }

  onRestaurantFilter(value: string): void {
    const state = new GridState();
    state.filter = new GridStateCompositeFilterDescriptor().and({
      field: 'name',
      operator: 'contains',
      value: value,
    });

    this.fetchRestaurants(state);
  }

  onReportingUnitFilter(value: string): void {
    const state = new GridState();
    state.filter = new GridStateCompositeFilterDescriptor().and({
      field: 'ruNm',
      operator: 'contains',
      value: value,
    }).and({
      field: 'conceptKey',
      operator: 'eq',
      value: this.selectedConcept?.id,
    });
    this.fetchReportingUnits(state);
  }
}
